import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, Building, User, Globe } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { Card, CardContent } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { CheckCircle2 } from "lucide-react";

export default function ContactForm() {
  const [formData, setFormData] = useState({
    companyName: "",
    role: "",
    email: "",
    country: "",
    type: "investor",
    companyDetails: "",
    message: "",
    consent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    const payload = {
      companyName: formData.companyName,
      role: formData.role,
      email: formData.email,
      country: formData.country,
      type: formData.type,
      companyDetails: formData.companyDetails,
      message: formData.message,
      consent: formData.consent
    };

    try {
      const endpoint = process.env.REACT_APP_CONTACT_ENDPOINT || "/api/contact";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      setSubmitted(true);
      
      // Reset after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          companyName: "",
          role: "",
          email: "",
          country: "",
          type: "investor",
          companyDetails: "",
          message: "",
          consent: false
        });
      }, 5000);
    } catch (err) {
      setError("There was a problem sending your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="bg-[#F5F7FA] py-24">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <Alert className="bg-[#24A19C]/10 border-[#24A19C]/30">
              <CheckCircle2 className="w-5 h-5 text-[#24A19C]" />
              <AlertDescription className="text-[#2C3E50] text-lg">
                Thank you for reaching out! We'll be in touch within 48 hours.
              </AlertDescription>
            </Alert>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F5F7FA] py-24">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-[#6B7C93]">
            Whether you're an investor or a company seeking capital, we'd like to hear from you.
          </p>
        </motion.div>

        <Card className="bg-white border-none shadow-xl">
          <CardContent className="p-8">
            {error && (
              <div className="mb-6">
                <Alert className="bg-red-50 border-red-200">
                  <AlertDescription className="text-red-700">
                    {error}
                  </AlertDescription>
                </Alert>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-[#2C3E50] font-medium">
                    Company / Fund Name *
                  </Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-3 w-5 h-5 text-[#6B7C93]" />
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => handleChange("companyName", e.target.value)}
                      className="pl-10 border-[#6B7C93]/30 focus:border-[#24A19C]"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role" className="text-[#2C3E50] font-medium">
                    Your Role *
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 w-5 h-5 text-[#6B7C93]" />
                    <Input
                      id="role"
                      value={formData.role}
                      onChange={(e) => handleChange("role", e.target.value)}
                      className="pl-10 border-[#6B7C93]/30 focus:border-[#24A19C]"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2C3E50] font-medium">
                    Email *
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-5 h-5 text-[#6B7C93]" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className="pl-10 border-[#6B7C93]/30 focus:border-[#24A19C]"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country" className="text-[#2C3E50] font-medium">
                    Country *
                  </Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-3 w-5 h-5 text-[#6B7C93] z-10" />
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleChange("country", e.target.value)}
                      className="pl-10 border-[#6B7C93]/30 focus:border-[#24A19C]"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C3E50] font-medium">
                  I am a(n) *
                </Label>
                <RadioGroup value={formData.type} onValueChange={(value) => handleChange("type", value)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="investor" id="investor" />
                    <Label htmlFor="investor" className="font-normal cursor-pointer">Investor</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="company" id="company" />
                    <Label htmlFor="company" className="font-normal cursor-pointer">Company seeking capital</Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.type === "company" && (
                <div className="space-y-2">
                  <Label htmlFor="companyDetails" className="text-[#2C3E50] font-medium">
                    Company Details
                  </Label>
                  <Select value={formData.companyDetails} onValueChange={(value) => handleChange("companyDetails", value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select details" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre-revenue">Pre-revenue</SelectItem>
                      <SelectItem value="0-5m">€0-5M revenue</SelectItem>
                      <SelectItem value="5-20m">€5-20M revenue</SelectItem>
                      <SelectItem value="20m+">€20M+ revenue</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="message" className="text-[#2C3E50] font-medium">
                  Message
                </Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  rows={5}
                  className="border-[#6B7C93]/30 focus:border-[#24A19C] resize-none"
                  placeholder="Tell us about your investment focus or company..."
                />
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="consent"
                  checked={formData.consent}
                  onCheckedChange={(checked) => handleChange("consent", checked)}
                  className="mt-1"
                  required
                />
                <Label htmlFor="consent" className="text-sm text-[#6B7C93] leading-relaxed cursor-pointer">
                  I consent to Miltech Capital processing my data in accordance with GDPR. 
                  I understand I may receive updates about defence technology investment opportunities.
                </Label>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !formData.consent}
                className="w-full bg-[#24A19C] hover:bg-[#1d8a85] text-white py-6 text-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-[#24A19C]/20"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message
                    <Send className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}